import Edit04Icon from "@redotech/redo-icons/edit-04.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { v3FiltersFormat } from "@redotech/redo-model/conversation-filters/conversation-filter-formats";
import { SystemView } from "@redotech/redo-model/conversation-filters/system-view";
import { CreateViewBody } from "@redotech/redo-model/create-view-body";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { UpdateViewBody } from "@redotech/redo-model/update-view-body";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import { isEnumValue } from "@redotech/util/enum";
import { sinkPromise } from "@redotech/util/promise";
import { memo, useContext, useMemo, useState } from "react";
import {
  ActiveViewContext,
  EditingViewContext,
  SetEditingViewContext,
} from "../conversations-table-filters/active-view-context";
import { FinalizedFiltersContext } from "../conversations-table-filters/filters-context";
import { SaveViewModal } from "../conversations-table-filters/save-view-modal";
import { DeleteViewModal } from "../delete-view-modal";

export const ConversationsTableHeaderViewManagement = memo(
  function ConversationsTableHeaderViewManagement({
    viewMeaningfullyDiffersFromBase,
    handleCreateView,
    handleUpdateView,
  }: {
    viewMeaningfullyDiffersFromBase: boolean;
    handleCreateView: (body: CreateViewBody) => Promise<void>;
    handleUpdateView: (view: UpdateViewBody, viewId: string) => Promise<void>;
  }) {
    const view = useContext(ActiveViewContext);
    const [deleteViewModalOpen, setDeleteViewModalOpen] = useState(false);
    const viewBeingEdited = useContext(EditingViewContext);
    const filters = useContext(FinalizedFiltersContext);

    const viewIsSystemView = isEnumValue(view._id, SystemView);
    const setViewBeingEdited = useContext(SetEditingViewContext);

    const [editViewDropdownRef, setEditViewDropdownRef] =
      useState<HTMLElement | null>(null);
    const [editViewDropdownOpen, setEditViewDropdownOpen] = useState(false);

    const canCreateView = useHasPermission(SupportPermissions.CREATE_TEAM_VIEW);

    const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);

    const deleteViewItem: RedoCommandMenuItem = useMemo(() => {
      return {
        Icon: TrashIcon,
        text: "Delete view",
        onClick: () => {
          setDeleteViewModalOpen(true);
        },
        theme: "destructive",
      };
    }, [setDeleteViewModalOpen]);

    const editViewItem: RedoCommandMenuItem = useMemo(() => {
      return {
        text: "Edit view",
        onClick: () => {
          setViewBeingEdited(true);
        },
        Icon: Edit04Icon,
      };
    }, [setViewBeingEdited]);

    const showUpdateViewButton =
      canCreateView &&
      viewMeaningfullyDiffersFromBase &&
      !viewBeingEdited &&
      !viewIsSystemView;
    const showEditViewButton =
      !showUpdateViewButton &&
      canCreateView &&
      !viewIsSystemView &&
      !viewBeingEdited;

    return (
      <>
        <Flex>
          {canCreateView &&
            viewMeaningfullyDiffersFromBase &&
            !viewBeingEdited && (
              <RedoButton
                hierarchy="secondary"
                onClick={() => setSaveViewModalOpen(true)}
                text="Save as new view"
              />
            )}
          {showUpdateViewButton && (
            <RedoButtonDropdown
              dropdownOpen={editViewDropdownOpen}
              onClick={() => {
                const updatedView: UpdateViewBody = {
                  name: view.name,
                  isPrivate: !!view.user,
                  filters: v3FiltersFormat.write(filters),
                };
                sinkPromise(handleUpdateView(updatedView, view._id));
              }}
              ref={setEditViewDropdownRef}
              setDropdownOpen={setEditViewDropdownOpen}
              size="sm"
              text="Update view"
            >
              <RedoCommandMenu
                anchor={editViewDropdownRef}
                items={[editViewItem, deleteViewItem]}
                open={editViewDropdownOpen}
                setOpen={setEditViewDropdownOpen}
              />
            </RedoButtonDropdown>
          )}

          {showEditViewButton && (
            <RedoButtonDropdown
              dropdownOpen={editViewDropdownOpen}
              onClick={() => setViewBeingEdited(true)}
              ref={setEditViewDropdownRef}
              setDropdownOpen={setEditViewDropdownOpen}
              size="sm"
              text="Edit view"
            >
              <RedoCommandMenu
                anchor={editViewDropdownRef}
                items={[deleteViewItem]}
                open={editViewDropdownOpen}
                setOpen={setEditViewDropdownOpen}
              />
            </RedoButtonDropdown>
          )}
        </Flex>
        {saveViewModalOpen && (
          <SaveViewModal
            activeFilters={filters}
            cancel={() => setSaveViewModalOpen(false)}
            save={async (body) => {
              try {
                await handleCreateView(body);
                setSaveViewModalOpen(false);
              } catch (e) {
                console.error(e);
              }
            }}
          />
        )}
        {deleteViewModalOpen && (
          <DeleteViewModal
            open={deleteViewModalOpen}
            setOpen={(open) => {
              setDeleteViewModalOpen(open);
            }}
            view={view}
          />
        )}
      </>
    );
  },
);
